import i18n from '@/utils/i18n';
import { Buffer } from 'buffer';
import { captureException } from '@/utils/sentry';
import {
  CreditRequestBatchLineItemInput,
  SubmitCheckDepositCreditMemoInput,
  SubmitCheckDepositInvoiceInput,
  SubmitCheckDepositMutation,
  SubmitCheckDepositMutationVariables,
} from '@/types';
import { getCheckCaptureDeviceTracking } from '@/utils/check-capture-device-tracking';
import { getCodeFromGQLError } from '@/utils/apollo-client';
import { gql, useMutation } from '@apollo/client';
import { useCheckCaptureStore } from '@/stores/check-capture-store';
import { useState } from 'react';

export const SUBMIT_CHECK_DEPOSIT_MUTATION = gql`
  mutation SubmitCheckDeposit(
    $chatUUID: uuid!
    $creditMemos: [SubmitCheckDepositCreditMemoInput!]!
    $creditRequestBatchLineItems: [CreditRequestBatchLineItemInput!]
    $deviceTracking: DeviceTrackingHasuraInput!
    $frontS3Key: String!
    $invoices: [SubmitCheckDepositInvoiceInput!]!
    $memoNotes: String!
    $rearS3Key: String!
  ) {
    submitCheckDeposit(
      chat_id: $chatUUID
      credit_memos: $creditMemos
      credit_request_batch_line_items: $creditRequestBatchLineItems
      device_tracking: $deviceTracking
      front_s3_key: $frontS3Key
      invoices: $invoices
      memo_notes: $memoNotes
      rear_s3_key: $rearS3Key
    ) {
      alert_level
      alert_message
      error_code
      payment_batch_id
    }
  }
`;

export enum CheckSubmitErrorCode {
  CHECK_CAPTURE_NOT_ENABLED = 'CHECK_CAPTURE_NOT_ENABLED',
  DEPOSIT_ERROR = 'DEPOSIT_ERROR',
  JH_AUTH_FAILED = 'JH_AUTH_FAILED',
  JH_SERVICE_UNAVAILABLE = 'JH_SERVICE_UNAVAILABLE',
  LOCATION_REFERENCE_NOT_SET = 'LOCATION_REFERENCE_NOT_SET',
  SUPPLIER_NOT_FOUND = 'SUPPLIER_NOT_FOUND',
  VALIDATION_FAILED = 'VALIDATION_FAILED',
  VALIDATION_FAILED_CHECK_FRONT = 'VALIDATION_FAILED_CHECK_FRONT',
  VALIDATION_FAILED_CHECK_REAR = 'VALIDATION_FAILED_CHECK_REAR',
}

export enum CheckSubmitRecovery {
  RETAKE = 'RETAKE',
  RETRY = 'RETRY',
  START_OVER = 'START_OVER',
}

const checkSubmitRecoveryMap: Record<
  CheckSubmitErrorCode,
  CheckSubmitRecovery
> = {
  [CheckSubmitErrorCode.CHECK_CAPTURE_NOT_ENABLED]:
    CheckSubmitRecovery.START_OVER,
  [CheckSubmitErrorCode.DEPOSIT_ERROR]: CheckSubmitRecovery.RETRY,
  [CheckSubmitErrorCode.JH_AUTH_FAILED]: CheckSubmitRecovery.START_OVER,
  [CheckSubmitErrorCode.JH_SERVICE_UNAVAILABLE]: CheckSubmitRecovery.RETRY,
  [CheckSubmitErrorCode.LOCATION_REFERENCE_NOT_SET]:
    CheckSubmitRecovery.START_OVER,
  [CheckSubmitErrorCode.SUPPLIER_NOT_FOUND]: CheckSubmitRecovery.START_OVER,
  [CheckSubmitErrorCode.VALIDATION_FAILED]: CheckSubmitRecovery.RETAKE,
  [CheckSubmitErrorCode.VALIDATION_FAILED_CHECK_FRONT]:
    CheckSubmitRecovery.RETAKE,
  [CheckSubmitErrorCode.VALIDATION_FAILED_CHECK_REAR]:
    CheckSubmitRecovery.RETAKE,
};

export interface SubmitCheckDepositArgs {
  chatUUID: string;
  creditMemos: SubmitCheckDepositCreditMemoInput[];
  creditRequestBatchLineItems?: CreditRequestBatchLineItemInput[];
  invoices: SubmitCheckDepositInvoiceInput[];
  memoNotes?: string;
}

export type SubmitCheckDepositResult =
  | { ok: true; paymentBatchId: string }
  | {
      code: CheckSubmitErrorCode | null;
      message: string;
      ok: false;
      recovery: CheckSubmitRecovery;
    };

/**
 * Uploads the captured front and rear images to S3, then submits the check
 * deposit via the Hasura action. Reads the captured images and S3 keys from
 * `useCheckCaptureStore`.
 *
 * @example
 * const [submitCheckDeposit, { loading }] = useSubmitCheckDeposit();
 * const result = await submitCheckDeposit({
 *   chatUUID,
 *   creditMemos,
 *   invoices,
 *   memoNotes,
 * });
 */
export const useSubmitCheckDeposit = (): [
  (args: SubmitCheckDepositArgs) => Promise<SubmitCheckDepositResult>,
  { loading: boolean },
] => {
  const [mutate] = useMutation<
    SubmitCheckDepositMutation,
    SubmitCheckDepositMutationVariables
  >(SUBMIT_CHECK_DEPOSIT_MUTATION);
  const [loading, setLoading] = useState(false);

  const submitCheckDeposit = async (
    args: SubmitCheckDepositArgs,
  ): Promise<SubmitCheckDepositResult> => {
    setLoading(true);

    try {
      const { credentials, images } = useCheckCaptureStore.getState();
      if (!credentials || !images.frontImage || !images.rearImage) {
        return {
          code: null,
          message: i18n.t('check_capture.submit_failed'),
          ok: false,
          recovery: CheckSubmitRecovery.START_OVER,
        };
      }

      try {
        await Promise.all([
          putCheckImageToS3(credentials.frontUploadUrl, images.frontImage),
          putCheckImageToS3(credentials.rearUploadUrl, images.rearImage),
        ]);
      } catch (e) {
        captureException(e);
        return {
          code: null,
          message: i18n.t('check_capture.submit_failed_try_again'),
          ok: false,
          recovery: CheckSubmitRecovery.RETRY,
        };
      }

      const { data, errors } = await mutate({
        variables: {
          chatUUID: args.chatUUID,
          creditMemos: args.creditMemos,
          creditRequestBatchLineItems: args.creditRequestBatchLineItems ?? null,
          deviceTracking: getCheckCaptureDeviceTracking(),
          frontS3Key: credentials.frontS3Key,
          invoices: args.invoices,
          memoNotes: args.memoNotes ?? '',
          rearS3Key: credentials.rearS3Key,
        },
      });

      if (errors || !data?.submitCheckDeposit) {
        return {
          code: null,
          message: i18n.t('check_capture.submit_failed_try_again'),
          ok: false,
          recovery: CheckSubmitRecovery.RETRY,
        };
      }

      const { alert_message, error_code, payment_batch_id } =
        data.submitCheckDeposit;

      if (payment_batch_id) {
        return { ok: true, paymentBatchId: payment_batch_id };
      }

      const knownCode =
        error_code && error_code in checkSubmitRecoveryMap
          ? (error_code as CheckSubmitErrorCode)
          : null;
      const message =
        alert_message ?? i18n.t('check_capture.submit_failed_try_again');

      if (
        knownCode &&
        checkSubmitRecoveryMap[knownCode] === CheckSubmitRecovery.RETAKE
      ) {
        useCheckCaptureStore.getState().setImageValidationError({
          front:
            knownCode === CheckSubmitErrorCode.VALIDATION_FAILED_CHECK_FRONT ||
            knownCode === CheckSubmitErrorCode.VALIDATION_FAILED,
          message,
          rear:
            knownCode === CheckSubmitErrorCode.VALIDATION_FAILED_CHECK_REAR ||
            knownCode === CheckSubmitErrorCode.VALIDATION_FAILED,
        });
      }

      return {
        code: knownCode,
        message,
        ok: false,
        recovery: knownCode
          ? checkSubmitRecoveryMap[knownCode]
          : CheckSubmitRecovery.RETRY,
      };
    } catch (e) {
      const errorCode = getCodeFromGQLError(e);
      if (errorCode && errorCode in checkSubmitRecoveryMap) {
        const knownCode = errorCode as CheckSubmitErrorCode;
        return {
          code: knownCode,
          message: i18n.t('check_capture.submit_failed_try_again'),
          ok: false,
          recovery: checkSubmitRecoveryMap[knownCode],
        };
      }
      return {
        code: null,
        message: i18n.t('check_capture.submit_failed_try_again'),
        ok: false,
        recovery: CheckSubmitRecovery.RETRY,
      };
    } finally {
      setLoading(false);
    }
  };

  return [submitCheckDeposit, { loading }];
};

const putCheckImageToS3 = async (url: string, base64: string) => {
  const response = await fetch(url, {
    body: Buffer.from(base64, 'base64'),
    headers: { 'Content-Type': 'image/jpeg' },
    method: 'PUT',
  });
  if (!response.ok) {
    throw new Error(`S3 upload failed: ${response.status}`);
  }
};
